import { useCallback } from 'react';

import { styled } from '@mui/material';
import { generateUtilityClasses, IconButtonGhost } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { IcCloseSmallRegular } from '@kep/kcds-icons';

import type { MouseEvent } from 'react';

export interface PanelTabProps {
  label: string;
  active?: boolean;
  className?: string;
  closable?: boolean;
  onClick?: () => void;
  onClose?: () => void;
}

/**
 * Panel Tab Component for SplitDetailPanel
 *
 * Implements tab design based on KCDS Drawer base component.
 * - Active tab: white background, semibold text
 * - Inactive tab: gray background, regular text
 * - Each tab includes a close button
 *
 * @example
 * ```tsx
 * <PanelTab
 *   label="Zone Details"
 *   active={true}
 *   onClose={() => handleClose()}
 *   onClick={() => handleClick()}
 * />
 * ```
 */
export default function PanelTab(props: PanelTabProps) {
  const { t } = useTranslation();
  const { label, active = false, closable = true, onClose, onClick, className } = props;

  const handleCloseClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClose?.();
    },
    [onClose],
  );

  return (
    <PanelTabRoot className={className} onClick={onClick} active={active}>
      <PanelTabLabel active={active}>{label}</PanelTabLabel>
      {closable && (
        <IconButtonGhost
          size="sm"
          onClick={handleCloseClick}
          aria-label={t('layout:panel.action.close')}
        >
          <IcCloseSmallRegular />
        </IconButtonGhost>
      )}
    </PanelTabRoot>
  );
}

export const panelTabClasses = generateUtilityClasses('PanelTab', ['root', 'label']);

interface StyledProps {
  active?: boolean;
}

const PanelTabRoot = styled('div', {
  name: 'PanelTab',
  slot: 'root',
  target: panelTabClasses.root,
  shouldForwardProp: prop => prop !== 'active',
})<StyledProps>(({ theme, active }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  flexShrink: 0,
  alignItems: 'center',
  gap: 4,
  maxWidth: 160,
  height: 40,
  padding: '4px 8px 4px 16px',
  backgroundColor: active ? theme.color['base/white'] : 'transparent',
  cursor: 'default',
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.shortest,
  }),

  '&:hover': {
    backgroundColor: active ? theme.palette.background.paper : theme.palette.action.hover,
  },

  ...theme.mixins.focusRing('inside'),
}));

const PanelTabLabel = styled('span', {
  name: 'PanelTab',
  slot: 'label',
  target: panelTabClasses.label,
  shouldForwardProp: prop => prop !== 'active',
})<StyledProps>(({ theme, active }) => {
  const typography = active
    ? theme.typography['label/semibold/md']
    : theme.typography['label/regular/md'];

  return {
    ...typography,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  };
});
